<script setup lang="ts">
import { computed } from 'vue'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/shadcn'

// 빈 문자열 value를 위한 내부 플레이스홀더
// reka-ui SelectItem이 빈 문자열을 처리하지 못하는 버그 우회
const EMPTY_VALUE_PLACEHOLDER = '__EMPTY_STRING__'

export interface Option {
  value: string | number
  label: string
}

type StyleType =
  | 'default'   // 기본 스타일
  | 'error'     // 에러 상태
  | 'success'   // 성공 상태
  | 'warning'   // 경고 상태
  | 'sm'        // 작은 크기
  | 'lg'        // 큰 크기

const props = withDefaults(
  defineProps<{
    modelValue?: string | number
    options?: Option[]
    placeholder?: string
    disabled?: boolean
    required?: boolean
    name?: string
    id?: string
    multiple?: boolean
    class?: string
    /** 스타일 프리셋 */
    styletype?: StyleType
  }>(),
  {
    options: () => [],
    placeholder: '',
    disabled: false,
    required: false,
    multiple: false,
    styletype: 'default',
  },
)

const emit = defineEmits<{
  'update:modelValue': [value: string | number]
  'change': [value: string | number]
  'focus': [event: FocusEvent]
  'blur': [event: FocusEvent]
}>()

/**
 * styletype -> class 매핑
 */
const STYLE_PRESETS: Record<StyleType, { class: string }> = {
  default: { class: '' },
  error: { 
    class: 'border-destructive focus:ring-destructive',
  },
  success: { 
    class: 'border-green-500 focus:ring-green-500',
  },
  warning: { 
    class: 'border-amber-500 focus:ring-amber-500',
  },
  sm: { 
    class: 'h-7 text-xs px-2',
  },
  lg: { 
    class: 'h-12 text-base px-4',
  },
}

const triggerClass = computed(() => {
  const styleKey = props.styletype || 'default'
  const preset = STYLE_PRESETS[styleKey] ?? STYLE_PRESETS.default
  return [preset?.class, props.class].filter(Boolean).join(' ')
})

/**
 * 외부 value를 내부 value로 변환
 * 빈 문자열 -> __EMPTY_STRING__
 */
const toInternalValue = (value: string | number | undefined): string => {
  if (value === '' || value === undefined) {
    return EMPTY_VALUE_PLACEHOLDER
  }
  return String(value)
}

/**
 * 내부 value를 외부 value로 변환
 * __EMPTY_STRING__ -> ''
 */
const toExternalValue = (value: string): string | number => {
  if (value === EMPTY_VALUE_PLACEHOLDER) {
    return ''
  }
  return value
}

/**
 * 옵션의 value를 내부 형식으로 변환
 * 빈 문자열 value를 가진 옵션 -> __EMPTY_STRING__으로 변환
 */
const processedOptions = computed(() => {
  return props.options.map((option) => ({
    ...option,
    value: toInternalValue(option.value),
  }))
})

/**
 * 현재 modelValue를 내부 형식으로 변환
 */
const internalModelValue = computed(() => toInternalValue(props.modelValue))

const handleChange = (internalValue: string) => {
  const externalValue = toExternalValue(internalValue)
  emit('update:modelValue', externalValue)
  emit('change', externalValue)
}
</script>

<template>
  <Select
    :model-value="internalModelValue"
    :disabled="disabled"
    :required="required"
    :name="name"
    @update:model-value="(value) => handleChange(value as string)"
    @focus="emit('focus', $event as FocusEvent)"
    @blur="emit('blur', $event as FocusEvent)"
  >
    <SelectTrigger :id="id" :class="triggerClass">
      <SelectValue :placeholder="placeholder" />
    </SelectTrigger>
    <SelectContent>
      <SelectItem
        v-for="option in processedOptions"
        :key="option.value"
        :value="option.value"
      >
        {{ option.label }}
      </SelectItem>
    </SelectContent>
  </Select>
</template>
